<template>
  <v-dialog
    v-model="localDialog"
    width="35%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6">
        <span>{{
          isEntryMode ? "Add Project Training" : "Update Project Training"
        }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="TrainingSetup">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                variant="outlined"
                density="compact"
                bg-color="grey-lighten-4"
                v-model="project_id.value.value"
                :items="getProjectList.filter((item) => item.status == 1)"
                :item-title="
                  (item) =>
                    item && item.name && item.code
                      ? `${item.name} - ${item.code}`
                      : ''
                "
                item-value="id"
                :error-messages="project_id.errorMessage.value"
                autocomplete="off"
                clearable
              >
                <template #label>
                  Project <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>

              <v-text-field
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="name.value.value"
                :error-messages="name.errorMessage.value"
                clearable
              >
                <template #label>
                    Training Name <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

                <v-date-input
                    input-format="dd/mm/yyyy"
                    label="Date"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="date.value.value"
                    :error-messages="date.errorMessage.value"
                    autocomplete="off"
                    clearable
                ></v-date-input>

                <v-text-field
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="facilitators.value.value"
                    :error-messages="facilitators.errorMessage.value"
                    clearable
                >
                    <template #label>
                        Facilitators <span class="text-red-accent-4">*</span>
                    </template>
                </v-text-field>

                <v-text-field
                    label="Invitation Email"
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="invitation_email.value.value"
                    :error-messages="invitation_email.errorMessage.value"
                    clearable
                >
                </v-text-field>

              <v-text-field
                label="Remarks (Optional)"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                clearable
              ></v-text-field>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                variant="outlined"
                hide-details
                single-line
                color="primary"
                value="1"
              ></v-checkbox>
              <input v-model="training_id.value.value" type="hidden" />
            </v-col>
          </v-row>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";
import { fileServerUrl } from "@/store/api";

export default {
  name: "projectTrainingSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        name: "",
        code: "",
        remarks: "",
        status: "1",
        date: "",
        facilitators: "",
        invitation_email: "",
        project_id: null,
        training_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      errorMessages: "",
      loading: false,
      isEntryMode: true,
    };
  },
  computed: {
    getProjectList() {
      return this.$store.getters.getProjectList;
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.name.setValue(newData.name || "", false);
        this.date.setValue(newData.date || "", false);
        this.facilitators.setValue(newData.facilitators || "", false);
        this.invitation_email.setValue(newData.invitation_email || "", false);
        this.remarks.setValue(newData.remarks || "", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.project_id.setValue(newData.project_id || "", false);
        this.training_id.setValue(newData.id || "", false);
      },
    },
  },
  created() {
    this.$store.dispatch("getProjectList");
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        name: yup.string().min(1).max(50).required().trim().label("Name"),
        facilitators: yup.string().min(1).max(50).required().trim().label("Facilitators"),
        invitation_email: yup.string().email().max(100).nullable().trim().label("Invitation Email"),
        remarks: yup.string().max(250).nullable().trim().label("Remarks"),
        project_id: yup.string().min(1).max(10).required().trim().label("Project"),
        date: yup.date().required().label("Date"),
      }),
    });

    const name = useField("name");
    const date = useField("date");
    const facilitators = useField("facilitators");
    const invitation_email = useField("invitation_email");
    const status = useField("status");
    const remarks = useField("remarks");
    const project_id = useField("project_id");
    const training_id = useField("training_id");
    const errorMessages = ref("");
    const loading = ref("");
    const store = useStore();

    const submit = handleSubmit((values) => {
      values.date = formattedDate(date.value.value);
      store
        .dispatch("saveUpdateProjectTraining", values)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          loading.value = true;
          store
            .dispatch("getProjectTrainingList")
            .then((data) => {
              loading.value = false;
            })
            .catch((error) => {
              loading.value = false;
              toast.error(error.response.data.message);
            });
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    });
    return {
      submit,
      name,
      date,
      facilitators,
      invitation_email,
      remarks,
      status,
      errorMessages,
      project_id,
      training_id,
      resetForm,
      loading
    };
  },
  mounted() {},
};
</script>
